import axios from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { Table, Container, Card, Modal, Form } from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import moment from 'moment';
import Chart from 'react-apexcharts';
import { AnimatePresence, motion } from 'framer-motion';


const SchemeTable = () => {
    const [schemasList, setSchemasList] = useState([]);
    // Dropdown state reserved for future brand filter (commented to avoid lints until used)
    // const [schemasListDropDown, setSchemasListDropDown] = useState([]);
    // const [selectedDropDown, setSelectedDropDown] = useState("");
    const [expandedBrands, setExpandedBrands] = useState(new Set());
    const [loader, setLoader] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [activeItem, setActiveItem] = useState(null);
    const [formDate, setFormDate] = useState(moment().format('YYYY-MM-DD'));
    const [formAmount, setFormAmount] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [historyItem, setHistoryItem] = useState(null);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyRows, setHistoryRows] = useState([]);

    useEffect(() => {
        getSchemasReceivablesList();
        // getSchemasReceivablesDropDownList();
    }, []);

    const getSchemasReceivablesList = async () => {
        let response = await axios('https://mis-api.happimobiles.com/api/schemes/getSchemeReceivables');
        let result = await response.data;

        const toNumber = (value) => {
            const numeric = Number(String(value ?? '').replace(/[,\s]/g, ''));
            return Number.isFinite(numeric) ? numeric : 0;
        };

        const normalized = Array.isArray(result)
            ? result.map((item) => ({
                  ...item,
                  ftd_qty: toNumber(item?.ftd_qty),
                  mtd_qty: toNumber(item?.mtd_qty),
                  sellout_disc_pct: toNumber(item?.sellout_disc_pct),
                  sellout_disc_value: toNumber(item?.sellout_disc_value),
                  sellout_spike_each_qty: toNumber(item?.sellout_spike_each_qty),
                  sellout_spike: toNumber(item?.sellout_spike),
                  sellout_total: toNumber(item?.sellout_total),
              }))
            : [];

        setSchemasList(normalized);
        setLoader(false);
    };

    // const getSchemasReceivablesDropDownList = async () => {
    //     let response = await axios('https://mis-api.happimobiles.com/api/schemes/getSchemeDropDown');
    //     let result = await response.data;
    //     setSchemasListDropDown(result.data);
    // };

    // Dropdown options currently not rendered

    const filteredList = useMemo(
        () => schemasList,
        [schemasList]
    );

    const groupedByBrand = useMemo(() => {
        const map = {};
        filteredList.forEach(item => {
            const key = item.brand_name || 'Unknown';
            if (!map[key]) {
                map[key] = {
                    items: [],
                    totals: {
                        ftd_qty: 0,
                        mtd_qty: 0,
                        sellout_disc_value: 0,
                        sellout_spike_each_qty: 0,
                        sellout_spike: 0,
                        sellout_total: 0,
                    },
                };
            }
            map[key].items.push(item);
            const t = map[key].totals;
            t.ftd_qty += item.ftd_qty || 0;
            t.mtd_qty += item.mtd_qty || 0;
            t.sellout_disc_value += item.sellout_disc_value || 0;
            t.sellout_spike_each_qty += item.sellout_spike_each_qty || 0;
            t.sellout_spike += item.sellout_spike || 0;
            t.sellout_total += item.sellout_total || 0;
        });
        return map;
    }, [filteredList]);

    const sortedBrands = useMemo(
        () => Object.entries(groupedByBrand).sort((a, b) => (b[1]?.totals?.sellout_total || 0) - (a[1]?.totals?.sellout_total || 0)),
        [groupedByBrand]
    );

    const pieSeries = useMemo(() => sortedBrands.map(([, data]) => Number(data.totals.sellout_total || 0)), [sortedBrands]);
    const pieLabels = useMemo(() => sortedBrands.map(([brand]) => brand), [sortedBrands]);

    const pieOptions = useMemo(
        () => ({
            chart: { type: 'donut' },
            labels: pieLabels,
            legend: { position: 'bottom' },
            dataLabels: { enabled: false },
            tooltip: { enabled: false },
            plotOptions: {
                pie: {
                    donut: {
                        size: '65%',
                        labels: {
                            show: true,
                            value: {
                                show: true,
                                formatter: (val) => `₹${format2(val)}`,
                            },
                            total: {
                                show: true,
                                label: 'Total',
                                formatter: w => `₹${format2(w.globals.seriesTotals.reduce((a, b) => a + b, 0))}`,
                            },
                        },
                    },
                },
            },
        }),
        [pieLabels]
    );

    const exportToCSV = (data, filename = 'data.csv') => {
        if (!data?.length) return;
        const headers = Object.keys(data[0]).join(',');
        const rows = data.map(row => Object.values(row).join(',')).join('\n');
        const csvContent = `${headers}\n${rows}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleBrand = brand => {
        setExpandedBrands(prev => {
            const next = new Set(prev);
            if (next.has(brand)) next.delete(brand); else next.add(brand);
            return next;
        });
    };

    const format2 = value => Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const inr = value => `₹${format2(value)}`;

    const openModal = (item) => {
        setActiveItem(item);
        setFormDate(moment().format('YYYY-MM-DD'));
        setFormAmount('');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setActiveItem(null);
    };

    const todayStr = moment().format('YYYY-MM-DD');
    const amountLimit = activeItem?.sellout_total || 0;
    const isFuture = formDate > todayStr;
    const isAmountInvalid = Number(formAmount || 0) > Number(amountLimit || 0);

    const openHistory = async (item) => {
        setHistoryItem(item);
        setShowHistory(true);
        setHistoryLoading(true);
        try {
            const params = new URLSearchParams({
                brand_name: item?.brand_name || '',
                item_name: item?.item_name || '',
                scheme: item?.scheme || '',
            });
            const url = `https://mis-api.happimobiles.com/api/schemes/getSchemeReceivablesHistory?${params.toString()}`;
            const resp = await axios.get(url);
            const data = Array.isArray(resp?.data) ? resp.data : (resp?.data?.data || []);
            const normalized = data.map(row => ({
                date: row.date || row.txn_date || row.created_at,
                amount: Number(String(row.amount ?? 0).replace(/[,\s]/g, '')),
                ref_no: row.ref_no || row.reference_no || row.reference || '-',
            }));
            setHistoryRows(normalized);
        } catch (e) {
            setHistoryRows([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const closeHistory = () => {
        setShowHistory(false);
        setHistoryItem(null);
        setHistoryRows([]);
    };


  return (
    <>
    <Container fluid className="my-4">
  <Card className="shadow-lg p-4 rounded-4 border-0">
    <h3 className="mb-4 fw-bold text-primary">Scheme Receivables</h3>

    <div className="d-flex justify-content-end mb-3 gap-5">
      {/* <Select
      options={getDropDownValues}
      className='w-50'
      value={selectedDropDown}
      isClearable
      onChange={setSelectedDropDown}
      isDisabled={loader}
      /> */}
      <button
        className="btn btn-danger shadow-sm d-flex align-items-center gap-2"
        onClick={() => exportToCSV(schemasList)}
        disabled={schemasList.length === 0}
      >
        <i className="bi bi-file-earmark-arrow-down"></i> Export CSV
      </button>
    </div>

    {!loader && (
      <div className="row mb-4">
        <div className="col-12 col-lg-6">
          <Card className="p-3 shadow-sm">
            <h6 className="fw-semibold mb-3">Brand-wise Pending Balance</h6>
            <Chart options={pieOptions} series={pieSeries} type="donut" height={300} />
          </Card>
        </div>
      </div>
    )}

    {loader ? (
      <div className="d-flex justify-content-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    ) : (
      <div className="table-responsive">
        <Table bordered hover className="align-middle shadow-sm">
          <thead className="table-primary text-center">
            <tr>
              <th>Brand Name</th>
              <th>Item Name</th>
              <th>Scheme</th>
              <th>From Date</th>
              <th className="text-nowrap">To Date</th>
              <th>FTD Sale Quantity</th>
              <th>MTD Sale Quantity</th>
              <th>Sell Discount Percentage</th>
            <th>Sell Discount Value</th>
              <th>Sellout Spike/Qty</th>
              <th>Sellout Spike</th>
              <th>Sellout Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedBrands.map(([brand, data]) => (
              <React.Fragment key={brand}>
                <tr
                  className="table-light text-center"
                  onClick={() => toggleBrand(brand)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="text-start fw-bold">
                    <i className={`bi ${expandedBrands.has(brand) ? 'bi-chevron-up' : 'bi-chevron-down'} me-2`}></i>
                    {brand}
                  </td>
                  <td colSpan={4} className="text-start text-muted">Click to expand</td>
                  <td>{format2(data.totals.ftd_qty)}</td>
                  <td>{format2(data.totals.mtd_qty)}</td>
                  <td>-</td>
                  <td>{format2(data.totals.sellout_disc_value)}</td>
                  <td>{format2(data.totals.sellout_spike_each_qty)}</td>
                  <td>{format2(data.totals.sellout_spike)}</td>
                  <td className="fw-bold">{inr(data.totals.sellout_total)}</td>
                  <td>-</td>
                </tr>
                <AnimatePresence>
                  {expandedBrands.has(brand) && (
                    <>
                      {data.items.map((item, index) => (
                        <motion.tr
                          key={`${brand}-${index}`}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="text-center"
                        >
                          <td>{item?.brand_name}</td>
                          <td>{item?.item_name}</td>
                          <td>{item?.scheme}</td>
                          <td>{moment(item?.from_date).format('DD-MM-YY')}</td>
                          <td>{moment(item?.to_date).format('DD-MM-YY')}</td>
                          <td>{format2(item.ftd_qty)}</td>
                          <td>{format2(item.mtd_qty)}</td>
                          <td>{format2(item.sellout_disc_pct)}</td>
                          <td>{format2(item.sellout_disc_value)}</td>
                          <td>{format2(item.sellout_spike_each_qty)}</td>
                          <td>{format2(item.sellout_spike)}</td>
                          <td>{inr(item.sellout_total)}</td>
                          <td className="d-flex gap-2 justify-content-center">
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center"
                              onClick={() => openModal(item)}
                              title="Add"
                              aria-label="Add"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                              </svg>
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center"
                              onClick={() => openHistory(item)}
                              title="History"
                              aria-label="History"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 8v5l3 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M21 12a9 9 0 1 1-9-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                <path d="M7 4v4H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </td>
                        </motion.tr>
                      ))}
                    </>
                  )}
                </AnimatePresence>
              </React.Fragment>
            ))}
          </tbody>
        </Table>
      </div>
    )}
  </Card>

</Container>

{/* Action Modal */}
<Modal show={showModal} onHide={closeModal} centered size="lg">
  <Modal.Header closeButton>
    <Modal.Title>Add Payment</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <div className="mb-3">
      <div className="small text-muted mb-1">Brand</div>
      <div className="fw-semibold">{activeItem?.brand_name || '-'}</div>
    </div>
    <div className="mb-2">
      <div className="small text-muted mb-1">Item</div>
      <div className="text-truncate" title={activeItem?.item_name || ''}>{activeItem?.item_name || '-'}</div>
    </div>
    <div className="mb-3">
      <div className="small text-muted mb-1">Scheme</div>
      <div className="text-truncate" title={activeItem?.scheme || ''}>{activeItem?.scheme || '-'}</div>
    </div>
    <Form>
      <Form.Group className="mb-3">
        <Form.Label>Date</Form.Label>
        <Form.Control
          type="date"
          value={formDate}
          max={todayStr}
          onChange={e => setFormDate(e.target.value)}
          isInvalid={isFuture}
        />
        <Form.Control.Feedback type="invalid">Date cannot be in the future.</Form.Control.Feedback>
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Amount (max {inr(amountLimit)})</Form.Label>
        <Form.Control
          type="number"
          step="0.01"
          min="0"
          value={formAmount}
          onChange={e => setFormAmount(e.target.value)}
          isInvalid={isAmountInvalid}
        />
        <Form.Control.Feedback type="invalid">Amount cannot exceed {inr(amountLimit)}.</Form.Control.Feedback>
      </Form.Group>
    </Form>
  </Modal.Body>
  <Modal.Footer>
    <button className="btn btn-outline-secondary" onClick={closeModal}>Cancel</button>
    <button
      className="btn btn-primary"
      onClick={closeModal}
      disabled={isFuture || isAmountInvalid || !formAmount}
    >
      Save
    </button>
  </Modal.Footer>
 </Modal>

 {/* History Modal */}
 <Modal show={showHistory} onHide={closeHistory} centered size="md">
   <Modal.Header closeButton>
     <Modal.Title>Transaction History</Modal.Title>
   </Modal.Header>
   <Modal.Body>
     <div className="mb-2">
       <div className="small text-muted">Brand</div>
       <div className="fw-semibold">{historyItem?.brand_name || '-'}</div>
     </div>
     <div className="mb-2">
       <div className="small text-muted">Item</div>
       <div className="text-truncate" title={historyItem?.item_name || ''}>{historyItem?.item_name || '-'}</div>
     </div>
     <div className="mb-3">
       <div className="small text-muted">Scheme</div>
       <div className="text-truncate" title={historyItem?.scheme || ''}>{historyItem?.scheme || '-'}</div>
     </div>
     {historyLoading ? (
       <div className="d-flex justify-content-center my-3"><Spinner animation="border" variant="primary" /></div>
     ) : (
       <div className="table-responsive">
         <Table bordered size="sm" className="align-middle">
           <thead className="table-light">
             <tr>
               <th>Date</th>
               <th className="text-end">Amount</th>
               <th>Ref No</th>
             </tr>
           </thead>
           <tbody>
             {historyRows.length === 0 ? (
               <tr><td colSpan={3} className="text-center text-muted">No transactions</td></tr>
             ) : (
               historyRows.map((row, idx) => (
                 <tr key={idx}>
                   <td>{row.date ? moment(row.date).format('DD-MM-YYYY') : '-'}</td>
                   <td className="text-end">{inr(row.amount)}</td>
                   <td>{row.ref_no}</td>
                 </tr>
               ))
             )}
           </tbody>
         </Table>
       </div>
     )}
   </Modal.Body>
   <Modal.Footer>
     <button className="btn btn-primary" onClick={closeHistory}>Close</button>
   </Modal.Footer>
 </Modal>
 </>
  );
};

export default SchemeTable;
